import { useCallback } from "react";
import { Link } from "react-router-dom";
import type { CoachPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { buttonVariants } from "@/components/ui/button";
import {
  DataTable, applyClientListParams,
  type DataTableColumn, type DataTableFetchParams,
} from "@/components/data-table";
import { SchoolFilter, useSchoolFilterIds } from "@/components/school-filter";
import { Badge } from "@/components/ui/badge";

// 148 -- Coaches list (school-scoped, mirrors Players). The coach profile at
// /coaches/:id hosts the BokSmart number + future coaching attributes.

const columns: DataTableColumn<CoachPublic>[] = [
  {
    key: "last_name",
    header: "Name",
    sortable: true,
    className: "font-medium",
    render: (c) => c.user_name ?? "--",
  },
  {
    key: "email",
    header: "Email",
    sortable: true,
    className: "text-muted-foreground",
    render: (c) => c.user_email,
  },
  {
    key: "teams",
    header: "Teams",
    render: (c) => c.team_count,
  },
  {
    key: "boksmart_number",
    header: "BokSmart #",
    sortable: true,
    render: (c) => (c.boksmart_number ? c.boksmart_number : <Badge variant="secondary">Not set</Badge>),
  },
  {
    key: "actions",
    header: "",
    headerClassName: "w-20",
    render: (c) => (
      <Link
        to={`/coaches/${c.id}`}
        className={buttonVariants({ variant: "outline", size: "sm" })}
        data-action="open-coach"
        onClick={(e) => e.stopPropagation()}
      >
        Profile
      </Link>
    ),
  },
];

export default function CoachesPage() {
  const schoolIds = useSchoolFilterIds();
  const schoolIdsKey = schoolIds.join(",");
  const isUnscoped = schoolIds.length === 0;

  // Fetcher resolves the active school scope:
  //  - single pinned school  -> pure server-side search/sort/pagination
  //  - multiple pinned schools -> fetch each unbounded, then merge + apply
  //    the same params client-side (server paging can't span endpoints)
  const fetcher = useCallback(
    async (params: DataTableFetchParams): Promise<{ data: CoachPublic[]; total: number }> => {
      if (schoolIds.length === 0) return { data: [], total: 0 };
      if (schoolIds.length === 1) {
        const r = await api.getCoaches(schoolIds[0], {
          page: params.page,
          per_page: params.per_page,
          search: params.search,
          sort: params.sort,
          order: params.order,
        });
        return { data: r.coaches, total: r.total ?? r.coaches.length };
      }
      const merged = (
        await Promise.all(schoolIds.map((id) => api.getCoaches(id).then((r) => r.coaches)))
      ).flat();
      return applyClientListParams(merged, params, {
        searchFields: (c) => [c.user_name, c.user_email, c.boksmart_number],
        sortAccessors: {
          last_name: (c) => c.user_name ?? "",
          email: (c) => c.user_email,
          boksmart_number: (c) => c.boksmart_number ?? "",
        },
        defaultSort: "last_name",
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by schoolIdsKey
    [schoolIdsKey],
  );

  return (
    <div className="flex flex-col gap-6" data-page="coaches">
      <div>
        <h1 className="text-2xl font-semibold">Coaches</h1>
        <p className="text-muted-foreground">Coach profiles and BokSmart accreditation.</p>
      </div>

      <SchoolFilter />

      {isUnscoped ? (
        <p className="text-sm text-muted-foreground">Pin or select a school to view its coaches.</p>
      ) : (
        <DataTable
          columns={columns}
          fetcher={fetcher}
          fetchKey={schoolIdsKey}
          rowKey={(c) => c.id}
          searchPlaceholder="Search name, email, BokSmart #..."
          defaultSort="last_name"
          emptyMessage="No coaches at the selected school(s) yet."
        />
      )}
    </div>
  );
}
